/**
 * SDL Chat Database — Dexie.js IndexedDB wrapper
 *
 * Stores chat messages and conversation metadata locally per player account.
 * Compound indexes [localPlayerId+peerId] enable multi-account isolation.
 */

import Dexie, { type Table } from 'dexie';
import type { ChatMessageType } from '@sdl/shared';

// ============================================================================
// INTERFACES
// ============================================================================

/** A chat message stored locally */
export interface StoredMessage {
  /** Server-assigned message ID (e.g. msg_<timestamp>_<rand>) */
  id: string;
  /** Current signed-in player's ID (for multi-account isolation) */
  localPlayerId: string;
  /** The other player in the conversation */
  peerId: string;
  /** Who sent this message */
  from: string;
  fromUsername: string;
  fromPicture?: string;
  /** Message content (text, CDN URL for images, or JSON for invites) */
  content: string;
  type: ChatMessageType;
  timestamp: number;
  /** 0 = unread, 1 = read */
  read: 0 | 1;
  /** Local blob URL for downloaded chat images */
  imageLocalBlob?: string;
}

/** Denormalized conversation metadata for the sidebar */
export interface Conversation {
  /** Current signed-in player's ID */
  localPlayerId: string;
  /** The other player's ID */
  peerId: string;
  peerUsername: string;
  peerPicture: string;
  lastMessageContent: string;
  lastMessageType: ChatMessageType;
  lastMessageTimestamp: number;
  unreadCount: number;
}

// ============================================================================
// DATABASE
// ============================================================================

class ChatDatabase extends Dexie {
  messages!: Table<StoredMessage>;
  conversations!: Table<Conversation>;

  constructor() {
    super('sdl-chat');

    this.version(1).stores({
      // Primary key: id
      // Compound indexes for per-conversation and per-account queries
      messages: 'id, [localPlayerId+peerId], [localPlayerId+peerId+timestamp], [localPlayerId+read]',
      // Compound primary key: [localPlayerId+peerId]
      conversations: '[localPlayerId+peerId], [localPlayerId+lastMessageTimestamp]',
    });
  }
}

/** Singleton database instance */
export const chatDb = new ChatDatabase();

// ============================================================================
// OPERATIONS
// ============================================================================

/**
 * Get all conversations for the current player, sorted by most recent first.
 */
export async function getConversations(localPlayerId: string): Promise<Conversation[]> {
  return chatDb.conversations
    .where('[localPlayerId+lastMessageTimestamp]')
    .between([localPlayerId, Dexie.minKey], [localPlayerId, Dexie.maxKey])
    .reverse()
    .toArray();
}

/**
 * Get messages for a specific conversation, paginated.
 * Returns newest-first; caller should reverse for display.
 */
export async function getMessages(
  localPlayerId: string,
  peerId: string,
  limit = 50,
  beforeTimestamp?: number,
): Promise<StoredMessage[]> {
  let collection = chatDb.messages
    .where('[localPlayerId+peerId+timestamp]')
    .between(
      [localPlayerId, peerId, Dexie.minKey],
      [localPlayerId, peerId, beforeTimestamp ?? Dexie.maxKey],
      true,
      beforeTimestamp ? false : true, // exclude the exact beforeTimestamp
    );

  return collection
    .reverse()
    .limit(limit)
    .toArray();
}

/**
 * Save a message and update the conversation metadata.
 */
export async function saveMessage(
  localPlayerId: string,
  msg: StoredMessage,
  peerUsername: string,
  peerPicture: string,
): Promise<void> {
  await chatDb.transaction('rw', chatDb.messages, chatDb.conversations, async () => {
    // Save the message
    await chatDb.messages.put(msg);

    // Upsert conversation
    const key = [localPlayerId, msg.peerId] as [string, string];
    const existing = await chatDb.conversations.get(key);

    if (existing) {
      await chatDb.conversations.update(key, {
        lastMessageContent: msg.content,
        lastMessageType: msg.type,
        lastMessageTimestamp: msg.timestamp,
        peerUsername,
        peerPicture,
        unreadCount: msg.read === 0 ? existing.unreadCount + 1 : existing.unreadCount,
      });
    } else {
      await chatDb.conversations.add({
        localPlayerId,
        peerId: msg.peerId,
        peerUsername,
        peerPicture,
        lastMessageContent: msg.content,
        lastMessageType: msg.type,
        lastMessageTimestamp: msg.timestamp,
        unreadCount: msg.read === 0 ? 1 : 0,
      });
    }
  });
}

/**
 * Delete all messages and conversation metadata for a peer.
 */
export async function deleteConversation(
  localPlayerId: string,
  peerId: string,
): Promise<void> {
  await chatDb.transaction('rw', chatDb.messages, chatDb.conversations, async () => {
    await chatDb.messages
      .where('[localPlayerId+peerId]')
      .equals([localPlayerId, peerId])
      .delete();

    await chatDb.conversations.delete([localPlayerId, peerId]);
  });
}

/**
 * Mark all messages from a peer as read and reset unread count.
 */
export async function markConversationRead(
  localPlayerId: string,
  peerId: string,
): Promise<void> {
  await chatDb.transaction('rw', chatDb.messages, chatDb.conversations, async () => {
    // Update all unread messages
    const unread = await chatDb.messages
      .where('[localPlayerId+peerId]')
      .equals([localPlayerId, peerId])
      .filter(m => m.read === 0)
      .toArray();

    for (const msg of unread) {
      await chatDb.messages.update(msg.id, { read: 1 });
    }

    // Reset conversation unread count
    await chatDb.conversations.update([localPlayerId, peerId], {
      unreadCount: 0,
    });
  });
}

/**
 * Get total unread message count across all conversations.
 */
export async function getUnreadCount(localPlayerId: string): Promise<number> {
  const conversations = await chatDb.conversations
    .where('[localPlayerId+lastMessageTimestamp]')
    .between([localPlayerId, Dexie.minKey], [localPlayerId, Dexie.maxKey])
    .toArray();

  return conversations.reduce((sum, c) => sum + c.unreadCount, 0);
}
